import { CallQueueType, EMPTY_AGENT_ID, EMPTY_QUEUE_ID } from "@/constants/queue";
import { SectionType } from "@/constants/section";
import { useAppData, UserRole } from "@/contexts/AppDataContext";
import { useDashboardData } from "@/contexts/DashboardContext";
import { useFilterCache } from "@/hooks/useFilterCache";
import { fetchKeyMetricsBulkRealtime } from "@/lib/axios/api/widgets.api";
import { DateRangeChange, FilterChangeEvent, QuickRange as FilterQuickRange, WidgetType } from "@/types";
import { calculateDateRange, DATE_RANGE_KEYS, getRecalculatedDatesIfNeeded, PERIODIC_QUICK_RANGES } from "@/utils/dateRangeUtils";
import { getMaxDayOfMonth } from "@/utils/dateUtils";
import { add, differenceInMonths, endOfWeek, startOfMonth, startOfQuarter, startOfWeek, startOfYear, sub } from "date-fns";
import React from "react";
import FilterBar from "../../common/FilterBar";
import StickyFilterBar from "../../common/StickyFilterBar";
import {
  AverageHandlingTimeTrendWidget,
  AverageWaitingTimeTrendWidget,
  CallVolumeTrendLineChart,
  SlaTrendWidget
} from "../../widgets/trends-insights";
import { getAgentIds } from "@/utils/agentUtils";

// Average Handle Time Trend (widget 11), SLA Trend (widget 12), and Average Wait Time Trend
// (widget 13) share this request window: "last N periods ending now" — Weekly: last 4 weeks,
// Monthly (default): last 6 months, Quarterly: last 4 quarters (12 months), Yearly: last 3
// years. `start` is calendar-aligned to the natural boundary of the first period in that
// window. These spans land within the matching backend granularity tier
// (2/14/120/210/730-day thresholds).
export const getAverageHandleTimeTrendWindow = (key: string, now: Date) => {
  let start: Date;
  let previousStart: Date;
  switch (key) {
    case DATE_RANGE_KEYS.WEEKLY: {
      const currentWeekStart = startOfWeek(now, { weekStartsOn: 0 }); // Sunday of the current week
      start = sub(currentWeekStart, { weeks: 3 }); // Sunday of the first of the last 4 weeks
      previousStart = sub(currentWeekStart, { weeks: 7 });
      break;
    }
    case DATE_RANGE_KEYS.QUARTERLY: {
      const currentQuarterStart = startOfQuarter(now);
      start = sub(currentQuarterStart, { months: 9 }); // 1st month of the first of the last 4 quarters
      previousStart = sub(currentQuarterStart, { months: 21 });
      break;
    }
    case DATE_RANGE_KEYS.YEARLY: {
      const currentYearStart = startOfYear(now);
      start = sub(currentYearStart, { years: 2 }); // Jan 1 of the first of the last 3 years
      previousStart = sub(currentYearStart, { years: 5 });
      break;
    }
    case DATE_RANGE_KEYS.MONTHLY:
    default: {
      const currentMonthStart = startOfMonth(now);
      start = sub(currentMonthStart, { months: 5 }); // 1st of the first of the last 6 months
      previousStart = sub(currentMonthStart, { months: 11 });
      break;
    }
  }
  // "start" is the calendar-aligned first day of the range, in local time, converted to UTC;
  // "end" is the current moment converted to UTC (Date#toISOString always yields the UTC instant).
  return {
    start: start.toISOString(),
    end: now.toISOString(),
    previousStart: previousStart.toISOString(),
    previousEnd: start.toISOString(),
  };
};

export const calculateSpecificPreviousDateRange = (
  from: Date,
  to: Date,
  key: string,
  fullPeriod: boolean = false
): { previousFrom: Date; previousTo: Date } | null => {
  let previousFrom: Date;
  let previousTo: Date;

  const isPeriodicRange =
    DATE_RANGE_KEYS.WEEKLY === key ||
    DATE_RANGE_KEYS.MONTHLY === key ||
    DATE_RANGE_KEYS.QUARTERLY === key ||
    DATE_RANGE_KEYS.YEARLY === key ||
    DATE_RANGE_KEYS.LAST_QUARTERS === key;
  if (!isPeriodicRange) return null;

  switch (key) {
    case DATE_RANGE_KEYS.WEEKLY:
      previousFrom = sub(from, { weeks: 1 });
      previousTo = sub(to, { weeks: 1 });
      if (fullPeriod) {
        previousTo = endOfWeek(previousFrom, { weekStartsOn: 0 });
      }
      break;
    case DATE_RANGE_KEYS.MONTHLY:
      previousFrom = sub(from, { months: 1 });
      previousTo = sub(to, { months: 1 });
      if (fullPeriod) {
        previousTo = new Date(previousTo.getFullYear(), previousTo.getMonth(), getMaxDayOfMonth(previousTo));
      }
      break;
    case DATE_RANGE_KEYS.QUARTERLY:
      previousFrom = sub(from, { years: 1 });
      previousTo = sub(to, { years: 1 });
      if (fullPeriod) {
        previousTo = add(previousFrom, { months: 2 });
        previousTo = new Date(previousTo.getFullYear(), previousTo.getMonth(), getMaxDayOfMonth(previousTo));
      }
      break;
    case DATE_RANGE_KEYS.YEARLY:
      previousFrom = sub(from, { years: 1 });
      previousTo = sub(to, { years: 1 });

      if (fullPeriod) {
        previousTo = new Date(previousTo.getFullYear(), 11, 31);
      }
      break;
    case DATE_RANGE_KEYS.LAST_QUARTERS: {
      // Calculate the start of the current quarter
      const quarterStartMonth = Math.floor(from.getMonth() / 3) * 3;
      const currentQuarterStart = new Date(from.getFullYear(), quarterStartMonth, 1);

      // month gap between from and to
      const monthGap = differenceInMonths(to, from);
      // Calculate the start of the previous quarter
      previousFrom = sub(currentQuarterStart, { months: 3 });
      previousTo = add(previousFrom, { months: monthGap });
      // Get max day of month
      const maxDayOfMonth = getMaxDayOfMonth(previousTo);
      previousTo = new Date(previousTo.getFullYear(), previousTo.getMonth(), Math.min(maxDayOfMonth, to.getDate()));
      break;
    }
  }

  // Set time to start/end of day
  previousFrom?.setHours(0, 0, 0, 0);
  previousTo?.setHours(23, 59, 59, 999);
  return {
    previousFrom,
    previousTo
  };
};

interface TrendsSectionProps {
  isGeneralMode: boolean;
  filterClass?: string;
}

const TrendsSection: React.FC<TrendsSectionProps> = ({ isGeneralMode, filterClass }) => {
  const defaultDateRangeKey = DATE_RANGE_KEYS.MONTHLY;
  const { current } = useDashboardData();
  const { filters, saveFilters } = useFilterCache("trends-insights", current?.id);
  const { queueMicrosoftIds, uniqueAgents, userRole, queuesDataVersion } = useAppData();

  const agentIds = React.useMemo(() => getAgentIds(uniqueAgents), [uniqueAgents]);

  const getArrayOrAll = (array: string[]) => array?.length > 0 ? array : queueMicrosoftIds;

  // TCX-6504: a Manager's resolved scope for a dimension must never be sent as a literal `[]` —
  // the four trend widgets (11-14) disagree on what an empty list means (research.md R2), so an
  // empty array would leak tenant-wide data on some of them. Send the reserved, non-matching
  // sentinel instead, mirroring KeyMetricsTab.tsx's TCX-6501 fix for the sibling Key Metric tab.
  const resolveQueueIdsForRequest = (ids: string[]) => (ids.length > 0 ? ids : [EMPTY_QUEUE_ID]);
  const resolveAgentIdsForRequest = (ids: string[]) => (ids.length > 0 ? ids : [EMPTY_AGENT_ID]);

  const getQueueMicrosoftIds = (selectedQueueType: CallQueueType, queues: string[]) => {
    if (userRole === UserRole.ADMIN && queues.length === 0) {
      return null;
    }
    if (selectedQueueType !== CallQueueType.CALL_QUEUE) {
      return [];
    }
    return resolveQueueIdsForRequest(getArrayOrAll(queues));
  };

  const getAgentMicrosoftIds = () => (userRole === UserRole.ADMIN ? null : resolveAgentIdsForRequest(agentIds));

  const [dateRange, setDateRange] = React.useState<DateRangeChange>({
    key: filters?.dateRangeKey ?? defaultDateRangeKey,
    startDate: null,
    endDate: null,
    previousStartDate: null,
    previousEndDate: null,
  });

  // Filter scope for the key-metrics request (widgets 11/12/13). Intentionally not wired to
  // the legacy POST /widgets/data bulk endpoint — that call was removed from the Trends tab.
  const [dateParams, setDateParams] = React.useState(() => {
    if (filters) {
      const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(
        filters.dateRangeKey,
        filters
      );

      setDateRange({
        key: filters?.dateRangeKey,
        startDate: startDate ?? null,
        endDate: endDate ?? null,
        previousStartDate: previousStartDate ?? null,
        previousEndDate: previousEndDate ?? null,
      });
      return {
        queueMicrosoftIds: getQueueMicrosoftIds(filters.selectedQueueType, filters.queues),
        agentMicrosoftIds: getAgentMicrosoftIds(),
        timeStamp: new Date().toISOString()
      };
    }
    // No cached filters: Call Volume Trend (widget 14) still needs a real calendar-period
    // window on first render — AHT/SLA/AWT don't, since they compute their own independent
    // "last N periods" window (getAverageHandleTimeTrendWindow) instead of reading dateRange.
    const range = calculateDateRange(defaultDateRangeKey);
    setDateRange({
      key: defaultDateRangeKey,
      startDate: range.from.toISOString(),
      endDate: range.to.toISOString(),
      previousStartDate: range.previousFrom.toISOString(),
      previousEndDate: range.previousTo.toISOString(),
    });
    return {
      queueMicrosoftIds: userRole === UserRole.ADMIN ? null : resolveQueueIdsForRequest(queueMicrosoftIds),
      agentMicrosoftIds: getAgentMicrosoftIds(),
      timeStamp: new Date().toISOString()
    };
  });

  const [dateRangeKey, setDateRangeKey] = React.useState<string>(filters?.dateRangeKey ?? defaultDateRangeKey);

  // Refresh key-metrics when tab becomes active, or when with-agents-and-role poll detects a
  // real change (skip initial mount — dateParams already has a fresh timeStamp)
  const isFirstActiveRef = React.useRef(true);
  React.useEffect(() => {
    if (isFirstActiveRef.current) {
      isFirstActiveRef.current = false;
      return;
    }
    if (isGeneralMode) {
      setDateParams(prev => ({
        ...prev,
        // TCX-6069: this used to only bump timeStamp, keeping the *previous* resolved
        // queueMicrosoftIds/agentMicrosoftIds — so an agent added to the role's scope in the
        // same Portal change that removed another one never showed up here until the user
        // manually touched the date range or filter (handleFilterChange is the only other place
        // that recomputes these). Recompute them fresh from the just-updated role scope so this
        // trigger alone reflects the new membership, not just a re-fetch of the stale one.
        queueMicrosoftIds: getQueueMicrosoftIds(filters?.selectedQueueType as CallQueueType, filters?.queues ?? []),
        agentMicrosoftIds: getAgentMicrosoftIds(),
        timeStamp: new Date().toISOString()
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed only on
    // isGeneralMode/queuesDataVersion (the "something real changed" signal); filters/agentIds/
    // userRole are read fresh from closure each time this fires. Listing them as deps would
    // also re-fire this effect right after handleFilterChange updates `filters` via saveFilters
    // — redundant (same result) but bumps timeStamp again and double-fetches every widget.
  }, [isGeneralMode, queuesDataVersion]);

  const handleFilterChange = (event: FilterChangeEvent) => {
    setDateRangeKey(event.dateRange.key);

    // Recalculate dates for dynamic date ranges to ensure freshness
    const recalculatedDates = getRecalculatedDatesIfNeeded(event.dateRange.key, {
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
    });

    setDateRange({
      key: event.dateRange.key,
      startDate: recalculatedDates.startDate,
      endDate: recalculatedDates.endDate,
      previousStartDate: recalculatedDates.previousStartDate,
      previousEndDate: recalculatedDates.previousEndDate,
    });
    setDateParams({
      queueMicrosoftIds: getQueueMicrosoftIds(event.selectedQueueType, event.queues),
      agentMicrosoftIds: getAgentMicrosoftIds(),
      timeStamp: new Date().toISOString()
    });

    // persist
    saveFilters({
      dateRangeKey: event.dateRange.key,
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
      queues: event.queues ?? [],
      agents: event.agents ?? [],
      selectedQueueType: event.selectedQueueType
    });
  };

  // Average Handle Time Trend (widget 11), SLA Trend (12), Average Wait Time Trend (13), and
  // Call Volume Trend (14) are sourced together from a single POST /api/widgets/key-metrics
  // request, sharing AHT's "last N periods ending now" window. Call Volume Trend's period count
  // is derived server-side from this same previousStart/previousEnd span, so no separate
  // granularity selection needs to be sent.
  const [averageHandlingTimeTrend, setAverageHandlingTimeTrend] = React.useState<Array<Record<string, unknown>>>([]);
  const [slaTrend, setSlaTrend] = React.useState<Array<Record<string, unknown>>>([]);
  const [averageWaitingTimeTrend, setAverageWaitingTimeTrend] = React.useState<Array<Record<string, unknown>>>([]);
  const [callVolumeTrend, setCallVolumeTrend] = React.useState<Array<Record<string, unknown>>>([]);
  const [trendWidgetsLoading, setTrendWidgetsLoading] = React.useState(true);
  const [trendWidgetsError, setTrendWidgetsError] = React.useState<string | null>(null);
  const [averageHandlingTimeTrendRange, setAverageHandlingTimeTrendRange] = React.useState<{ from: Date; to: Date }>(
    () => {
      const now = new Date();
      const window = getAverageHandleTimeTrendWindow(dateRangeKey, now);
      return { from: new Date(window.start), to: now };
    }
  );

  React.useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const offsetInMinutes = -new Date().getTimezoneOffset();
    const now = new Date();
    const window = getAverageHandleTimeTrendWindow(dateRangeKey, now);
    setAverageHandlingTimeTrendRange({ from: new Date(window.start), to: now });

    const payload = {
      start: window.start,
      end: window.end,
      previousStart: window.previousStart,
      previousEnd: window.previousEnd,
      offsetInMinutes,
      // null = no restriction (Admin). For a Manager, dateParams.queueMicrosoftIds/agentMicrosoftIds
      // are never a literal [] (TCX-6504) — resolveQueueIdsForRequest/resolveAgentIdsForRequest
      // substitute EMPTY_QUEUE_ID/EMPTY_AGENT_ID for an empty resolved scope, so the four trend
      // widgets' own divergent empty-list conventions (research.md R2) never come into play here.
      queueIds: dateParams.queueMicrosoftIds,
      agentIds: dateParams.agentMicrosoftIds,
      directAgentIds: null,
    };

    setTrendWidgetsLoading(true);
    setTrendWidgetsError(null);

    fetchKeyMetricsBulkRealtime(payload, [11, 12, 13, 14], { signal: controller.signal })
      .then((res) => {
        if (!isMounted) return;
        setAverageHandlingTimeTrend((res?.["11"] as Array<Record<string, unknown>>) ?? []);
        setSlaTrend((res?.["12"] as Array<Record<string, unknown>>) ?? []);
        setAverageWaitingTimeTrend((res?.["13"] as Array<Record<string, unknown>>) ?? []);
        setCallVolumeTrend((res?.["14"] as Array<Record<string, unknown>>) ?? []);
      })
      .catch((err) => {
        if (!isMounted) return;
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setTrendWidgetsError(err?.message || "Failed to load data");
      })
      .finally(() => {
        if (isMounted) setTrendWidgetsLoading(false);
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [dateRangeKey, dateParams]);

  const getRangeType = (key: string): 'weekly' | 'monthly' | 'quarterly' | 'yearly' => {
    switch (key) {
      case DATE_RANGE_KEYS.WEEKLY:
        return 'weekly';
      case DATE_RANGE_KEYS.MONTHLY:
        return 'monthly';
      case DATE_RANGE_KEYS.QUARTERLY:
        return 'quarterly';
      case DATE_RANGE_KEYS.YEARLY:
        return 'yearly';
      default:
        return 'weekly';
    }
  };

  return (
    <div className="w-full">
      <StickyFilterBar className={filterClass}>
        <FilterBar
          onFilterChange={handleFilterChange}
          initialDateRangeKey={dateRangeKey}
          initialSelectedQueues={filters?.queues}
          initialSelectedAgents={filters?.agents}
          initialSelectedQueueType={filters?.selectedQueueType}
          quickRanges={PERIODIC_QUICK_RANGES as unknown as FilterQuickRange[]}
          initialDateRange={dateRange}
          sectionType={SectionType.TRENDS}
        />
      </StickyFilterBar>
      <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4">
        <CallVolumeTrendLineChart
          data={{ id: 'call-volume-trend', type: WidgetType.CALL_VOLUMES, title: 'Call Volume Trend', value: callVolumeTrend }}
          isGeneralMode={isGeneralMode}
          loading={trendWidgetsLoading}
          error={trendWidgetsError}
          rangeType={getRangeType(dateRangeKey)}
          dateRange={averageHandlingTimeTrendRange}
        />
        <SlaTrendWidget
          data={{ id: 'sla-trend', type: WidgetType.AGENT_AVAILABILITY, title: 'SLA (%) Trend', value: slaTrend }}
          loading={trendWidgetsLoading}
        />
        <AverageWaitingTimeTrendWidget
          data={{ id: 'average-waiting-time-trend', type: WidgetType.AVG_WAITING_TIME, title: 'Average Wait Time Trend', value: averageWaitingTimeTrend }}
          isGeneralMode={isGeneralMode}
          loading={trendWidgetsLoading}
          error={trendWidgetsError}
          dateRange={averageHandlingTimeTrendRange}
        />
        <AverageHandlingTimeTrendWidget
          data={{ id: 'average-handling-time-trend', type: WidgetType.AVG_HANDLING_TIME, title: 'Average Handle Time Trend', value: averageHandlingTimeTrend }}
          isGeneralMode={isGeneralMode}
          loading={trendWidgetsLoading}
          error={trendWidgetsError}
          dateRange={averageHandlingTimeTrendRange}
        />
      </div>
    </div>
  );
};

export default TrendsSection;
